/**
 * LICENSE GLOBAL - MODERN HEADER JAVASCRIPT
 * Funzionalità interattive per l'header moderno
 */

class ModernHeader {
  constructor() {
    // Cache degli elementi DOM
    this.elements = {
      mobileMenuBtn: document.querySelector('.mobile-menu-btn'),
      navMenu: document.querySelector('.nav-menu'),
      searchInput: document.querySelector('.search-input'),
      searchWrapper: document.querySelector('.search-wrapper'),
      searchBtn: document.querySelector('.search-btn'),
      cartCount: document.querySelector('.cart-count')
    };
    
    // Stato
    this.state = {
      isMobileMenuOpen: false,
      isScrolling: false,
      lastScrollTop: 0,
      searchTimeout: null
    };
    
    // Inizializzazione
    this.init();
    this.bindEvents();
    this.setupScrollBehavior();
    this.setupCartUpdates();
  }

  init() {
    // Inizializzazione completata nel costruttore
  }

  bindEvents() {
    const { searchInput, searchBtn } = this.elements;
    
    // Il menu hamburger nativo del tema gestisce già la sua funzionalità
    // Non è necessario aggiungere event listener personalizzati

    // Search input events - Solo se non è dentro un predictive-search
    if (searchInput && !searchInput.closest('predictive-search')) {
      searchInput.addEventListener('focus', () => this.handleSearchFocus());
      searchInput.addEventListener('blur', () => this.handleSearchBlur());
      searchInput.addEventListener('input', (e) => this.handleSearchInput(e));
    }

    // Search form submission
    const searchForm = searchInput?.closest('form');
    if (searchForm && !searchForm.closest('predictive-search')) {
      searchForm.addEventListener('submit', (e) => this.handleSearchSubmit(e));
    }

    // Keyboard navigation
    document.addEventListener('keydown', (e) => this.handleKeyboard(e));

    // Update active nav link
    this.updateActiveNavLink();

    // Cart button interactions
    this.setupCartInteractions();
  }

  // Le funzioni del menu hamburger non sono più necessarie
  // perché usiamo quello nativo del tema

  handleSearchFocus() {
    this.elements.searchWrapper?.classList.add('focused');
  }

  handleSearchBlur() {
    // Delay removing focus to allow click events on search button
    setTimeout(() => {
      this.elements.searchWrapper?.classList.remove('focused');
    }, 150);
  }

  handleSearchInput(e) {
    const value = e.target.value.trim();
    const { searchBtn } = this.elements;
    
    // Add loading state to search button if there's text
    if (searchBtn) {
      searchBtn.classList.toggle('has-text', value.length > 0);
    }

    // Non eseguire la ricerca manuale se è dentro un predictive-search
    if (!this.elements.searchInput.closest('predictive-search')) {
      // Debounced search suggestions (optional)
      this.debounceSearch(value);
    }
  }

  handleSearchSubmit(e) {
    const searchValue = this.elements.searchInput?.value.trim();
    
    if (!searchValue) {
      e.preventDefault();
      this.elements.searchInput?.focus();
      return;
    }

    // Add loading state
    this.elements.searchBtn?.classList.add('loading');
    
    // Remove loading state after a short delay
    setTimeout(() => {
      this.elements.searchBtn?.classList.remove('loading');
    }, 1000);
  }

  handleKeyboard(e) {
    // ESC key closes mobile menu
    if (e.key === 'Escape' && this.state.isMobileMenuOpen) {
      this.state.isMobileMenuOpen = false;
      document.body.style.overflow = '';
    }

    // ALT + S focuses search
    if (e.altKey && e.key === 's') {
      e.preventDefault();
      this.elements.searchInput?.focus();
    }
  }

  updateActiveNavLink() {
    const currentPath = window.location.pathname;
    const { navMenu } = this.elements;
    
    navMenu?.querySelectorAll('.nav-link').forEach(link => {
      link.classList.remove('active');
      const href = link.getAttribute('href');
      
      if (href === currentPath ||
          (href === '/' && (currentPath === '/' || currentPath === '')) ||
          (currentPath.startsWith('/collections/') && currentPath === href) ||
          (currentPath.startsWith('/pages/') && currentPath === href)) {
        link.classList.add('active');
      }
    });
  }

  setupScrollBehavior() {
    let scrollTimer = null;
    
    window.addEventListener('scroll', () => {
      if (!this.state.isScrolling) {
        document.body.classList.add('scrolled');
      }
      
      this.state.isScrolling = true;
      
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        this.state.isScrolling = false;
        
        // Remove scrolled class if at top
        if (window.pageYOffset === 0) {
          document.body.classList.remove('scrolled');
        }
      }, 100);
    }, { passive: true });
  }

  setupCartUpdates() {
    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      this.updateCartCount();
    });

    // Listen for cart drawer events
    document.addEventListener('cart:drawer:opened', () => {
      this.handleCartDrawerOpened();
    });

    // Initial cart count
    this.updateCartCount();
  }

  setupCartInteractions() {
    // NON AGGIUNGERE ALCUN LISTENER PERSONALIZZATO PER IL CARRELLO
    // Lasciare che il tema gestisca completamente l'apertura del minicart
    // attraverso il componente <mini-cart-popup> definito in assets/header.js
    
    // Il problema era che il nostro codice interferiva con il sistema nativo del tema
    // che utilizza già il componente mini-cart-popup per gestire l'apertura
    
    // Non facciamo assolutamente nulla qui, lasciamo che il tema gestisca tutto
  }

  updateCartCount() {
    const { cartCount } = this.elements;
    
    if (!cartCount) return;
    
    // Fetch cart count via AJAX or use Shopify's cart object
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const itemCount = cart.item_count;
        
        if (itemCount > 0) {
          cartCount.textContent = itemCount;
          cartCount.style.display = 'flex';
          cartCount.classList.add('updated');
          
          // Remove animation class after animation completes
          setTimeout(() => {
            cartCount.classList.remove('updated');
          }, 300);
        } else {
          cartCount.style.display = 'none';
        }
      })
      .catch(error => {
        console.error('Error updating cart count:', error);
      });
  }

  handleCartDrawerOpened() {
    // Add any custom behavior when cart drawer opens
    console.log('Cart drawer opened');
  }

  createRipple(event, element) {
    const ripple = document.createElement('span');
    const rect = element.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    ripple.style.width = ripple.style.height = size + 'px';
    ripple.style.left = x + 'px';
    ripple.style.top = y + 'px';
    ripple.classList.add('ripple');

    element.appendChild(ripple);

    setTimeout(() => {
      ripple.remove();
    }, 600);
  }

  debounceSearch(value) {
    clearTimeout(this.state.searchTimeout);
    
    this.state.searchTimeout = setTimeout(() => {
      if (value.length > 2) {
        // Implement search suggestions here
        console.log('Search suggestions for:', value);
      }
    }, 300);
  }

  // Public methods
  destroy() {
    const { searchInput } = this.elements;
    
    // Clean up event listeners
    searchInput?.removeEventListener('focus', this.handleSearchFocus);
    searchInput?.removeEventListener('blur', this.handleSearchBlur);
    
    // Reset state
    if (this.state.isMobileMenuOpen) {
      this.state.isMobileMenuOpen = false;
      document.body.style.overflow = '';
    }
    
    // Clear timeout
    clearTimeout(this.state.searchTimeout);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.modernHeader = new ModernHeader();
});

// Clean up on page unload
window.addEventListener('beforeunload', () => {
  if (window.modernHeader) {
    window.modernHeader.destroy();
  }
});

// Export for potential module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ModernHeader;
}